import { useCheckSomePermissions } from 'hooks/permissions'
import React from 'react'
import { Redirect, Route, RouteProps } from 'react-router-dom'

import { getToken } from '../services/auth'

interface RedirectToProps extends RouteProps {
    pathname: string
    redirectTo?: string
}

const RedirectTo: React.FC<RedirectToProps> = ({ pathname, redirectTo, location, ...props }) => {
    // Need to remove path and component from the props to redirect
    const { path, component, ...rest } = props
    return (
        <Route
            {...rest}
            render={() => (
                <Redirect
                    to={{
                        pathname,
                        search: redirectTo,
                        state: { from: location },
                    }}
                />
            )}
        />
    )
}

interface PrivateRouteProps extends RouteProps {
    permissions?: string[]
}

/**
 *
 * @param permissions List of permissions to check
 * @returns Redirection to login, 403 or the requested path
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ permissions, ...props }) => {
    const isAuthenticated = getToken() !== null
    const redirectToQuery = location.pathname
        ? `redirectTo=${encodeURIComponent(location.pathname + (location.search || ''))}`
        : undefined

    if (!isAuthenticated) {
        return <RedirectTo pathname="/login" redirectTo={redirectToQuery} {...props} />
    }

    if (permissions && permissions.length > 0) {
        return <PermissionCheckRoute permissions={permissions} {...props} />
    }

    return <Route {...props} />
}

interface PermissionCheckRouteProps extends RouteProps {
    permissions: string[]
}

const PermissionCheckRoute: React.FC<PermissionCheckRouteProps> = ({ permissions, ...props }) => {
    const isPermissionAllowed = useCheckSomePermissions(permissions)

    if (!isPermissionAllowed) {
        return <RedirectTo pathname="/403" {...props} />
    }

    return <Route {...props} />
}

export default PrivateRoute
